import { takeReduxPersisted } from '@/lib/takeReduxPersisted';
import type { ActivePushTopic, PushTopic } from '@/push/types';
import { createStore } from 'cross-state';

export interface PushState {
    supported: boolean | undefined;
    permission: PermissionState | undefined;
    topics: PushTopic[];
    topicsActive: ActivePushTopic[];
    inProgress: boolean;
    token?: string | null;
}

export const pushStore = createStore<PushState>({
    supported: undefined,
    permission: undefined,
    topics: [],
    topicsActive: [],
    inProgress: false,
});

export const updatePushState = (update: Partial<PushState>) => pushStore.set((state) => ({ ...state, ...update }));

/** Older versions stored store topics as plain strings and global topics separately in `topicsGlobal`. */
export function migrateLegacyPushState(legacy: Record<string, unknown>): Pick<PushState, 'topics' | 'topicsActive' | 'token'> {
    const token = typeof legacy.token === 'string' ? legacy.token : undefined;
    const topics = Array.isArray(legacy.topics) ? (legacy.topics as (PushTopic | string)[]) : [];
    const topicsActive = Array.isArray(legacy.topicsActive) ? (legacy.topicsActive as ActivePushTopic[]) : [];

    if (!topics.some((x) => typeof x === 'string')) {
        return { topics: topics as PushTopic[], topicsActive, token };
    }

    const topicsGlobal = Array.isArray(legacy.topicsGlobal) ? (legacy.topicsGlobal as { topic: string; storeGuid: string | null }[]) : [];
    return {
        topics: [
            ...topics.flatMap((x): PushTopic[] => (typeof x === 'string' ? [{ type: 'store', topic: x }] : [])),
            ...topicsGlobal.map((x): PushTopic => ({ type: 'global', topic: x.topic, storeGuid: x.storeGuid })),
        ],
        topicsActive,
        token,
    };
}

// Loaded once and not synced live: each tab computes its own subscriptions and would unsubscribe the others' topics
const storageKey = 'pushTopics';

function readStored(): Record<string, unknown> | undefined {
    try {
        const stored: unknown = JSON.parse(localStorage.getItem(storageKey) ?? 'null');
        return stored && typeof stored === 'object' ? (stored as Record<string, unknown>) : undefined;
    } catch {
        return undefined;
    }
}

const stored = takeReduxPersisted('push') ?? readStored();
if (stored) {
    updatePushState(migrateLegacyPushState(stored));
}

pushStore.subscribe(
    ({ topics, topicsActive, token }) => {
        localStorage.setItem(storageKey, JSON.stringify({ topics, topicsActive, token }));
    },
    { runNow: !!stored },
);
